import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../../assets/logo.png";
import L from "../../../constants/baysportLabels";
import { LegalFooterLinks } from "../../Legal/LegalDocLink";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  buildLandingPriceHighlights,
  PLAN_POSITIONING,
  LANDING_PLANS_FALLBACK,
} from "../../../constants/planMarketing";
import platformAPI from "../../../services/platformApi";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import { yandexMetrikaParams } from "../../../analytics/yandexMetrika";
import {
  PRICING_LANDING_PAGE_TITLE,
  PRICING_LANDING_PAGE_DESCRIPTION,
  PRICING_LANDING_PAGE_KEYWORDS,
  PRICING_LANDING_PATH,
  LANDING_OG_IMAGE,
  LANDING_OG_IMAGE_ALT,
  LANDING_PRICE_FROM,
  buildPricingLandingJsonLd,
} from "../HomeLanding/landingSeo";
import { SITE_ORIGIN } from "../../../config/site";
import Preloader from "../../common/Preloader";
import Seo from "../../Seo";
import s from "../CleanAdLanding/cleanAdLanding.module.css";

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

const TRIAL_INCLUDES = [
  "Журнал посещаемости и расписание",
  "Абонементы, должники и выручка",
  "Кабинет родителей и тренеров",
  "Соцсеть, брендинг и онлайн-оплата",
  "Лимиты тарифа «Максимальный» на время триала",
];

const WHO_FITS = [
  {
    title: "Стартовый",
    text: "Один тренер и своя группа. Журнал, абонементы, чат и кабинет родителя.",
  },
  {
    title: "Расширенный",
    text: "Команда до 5 тренеров, соцсеть, оформление портала и оплата картой.",
  },
  {
    title: "Максимальный",
    text: "Крупная секция без жёстких лимитов по команде и участникам.",
  },
];

const FAQ = [
  {
    q: "Что входит в пробный период?",
    a: `Первые ${TRIAL_DAYS} дней — доступ как на тарифе «Максимальный»: журнал, абонементы, кабинеты, соцсеть и оплата картой. Карту привязывать не нужно.`,
  },
  {
    q: "Нужен ли программист?",
    a: "Нет. Всё работает в браузере телефона и компьютера. Страницу можно сохранить на главный экран — как обычное приложение.",
  },
  {
    q: "Можно ли вести секцию только с телефона?",
    a: "Да. Журнал, абонементы, расписание и кабинет открываются с телефона. Компьютер в зале не обязателен.",
  },
  {
    q: "Что будет с данными после триала?",
    a: "Данные остаются в вашем клубе. Выбираете тариф под размер секции и продолжаете работу — базу заново заводить не нужно.",
  },
  {
    q: "Сколько стоит дальше?",
    a: `После пробного периода — от ${LANDING_PRICE_FROM} ₽ в месяц за клуб. Точная цена зависит от тарифа: Старт, Клуб или Про.`,
  },
];

export default function PricingLanding() {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    yandexMetrikaParams({ landing: "pricing" });
  }, []);

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((r) => {
        const loaded = Array.isArray(r?.plans) ? r.plans : [];
        setPlans(loaded.length ? loaded : LANDING_PLANS_FALLBACK);
      })
      .catch(() => setPlans(LANDING_PLANS_FALLBACK))
      .finally(() => setPlansLoading(false));
  }, []);

  const featuredCode = useMemo(() => {
    const withBadge = plans.find((p) => p.badge);
    return withBadge?.code || plans[1]?.code || plans[0]?.code;
  }, [plans]);

  const onTry = () => trackTryClick({ landing: "pricing" });

  return (
    <div className={s.page}>
      <Seo
        title={PRICING_LANDING_PAGE_TITLE}
        description={PRICING_LANDING_PAGE_DESCRIPTION}
        keywords={PRICING_LANDING_PAGE_KEYWORDS}
        canonicalUrl={`${SITE_ORIGIN}${PRICING_LANDING_PATH}`}
        ogImage={LANDING_OG_IMAGE}
        ogImageAlt={LANDING_OG_IMAGE_ALT}
        jsonLd={buildPricingLandingJsonLd(FAQ)}
      />

      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <a
            href={buildClinicPortalUrl(tenant.subdomain, getDashboardPath(role))}
            className={`${s.btn} ${s.btnPrimary}`}
          >
            Открыть кабинет
          </a>
        </div>
      ) : null}

      <header className={s.nav}>
        <Link to="/" className={s.navBrand}>
          <img src={logo} alt="" className={s.navLogo} />
          <span>{L.productName}</span>
        </Link>
        <div className={s.navActions}>
          <a href="#pricing" className={`${s.btn} ${s.btnGhost} ${s.navLink}`}>
            Тарифы
          </a>
          <a href="#trial" className={`${s.btn} ${s.btnGhost} ${s.navLink}`}>
            Пробный период
          </a>
          <Link to="/account/login" className={`${s.btn} ${s.btnGhost}`}>
            Войти
          </Link>
          <Link
            to="/registraciya"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTry}
          >
            Попробовать
          </Link>
        </div>
      </header>

      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroCopy}>
            <p className={s.heroEyebrow}>
              Тарифы · {TRIAL_DAYS} дней бесплатно
            </p>
            <h1 className={s.heroTitle}>
              Тарифы CRM
              <br />
              для спортивного клуба
            </h1>
            <p className={s.heroLead}>
              Три тарифа под размер секции — от одного тренера до крупной
              команды. Первые {TRIAL_DAYS} дней — полный доступ, карта не нужна.
            </p>
            <div className={s.heroActions}>
              <Link
                to="/registraciya"
                className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
                onClick={onTry}
              >
                Попробовать {TRIAL_DAYS} дней бесплатно
              </Link>
              <a href="#pricing" className={`${s.btn} ${s.btnSecondary} ${s.btnLg}`}>
                Смотреть цены
              </a>
            </div>
            <p className={s.heroNote}>
              От {LANDING_PRICE_FROM} ₽/мес · без разработчиков · с телефона
            </p>
          </div>
          <div className={s.heroVisual} aria-hidden>
            <div className={`${s.art} ${s.artMembership}`}>
              <div className={s.artTicket}>
                <span>От</span>
                <strong>{formatRub(LANDING_PRICE_FROM)} ₽</strong>
              </div>
              <div className={s.artCoin} />
            </div>
          </div>
        </div>

        <div className={s.benefitRow}>
          {WHO_FITS.map((item) => (
            <article key={item.title} className={s.benefitCard}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="pricing" className={s.section}>
        <h2 className={s.sectionTitle}>Сколько стоит</h2>
        <p className={s.sectionLead}>
          Цена за один клуб в месяц. После триала выбираете тариф — данные
          остаются.
        </p>
        {plansLoading ? (
          <Preloader variant="inline" label="Загрузка тарифов…" />
        ) : (
          <div className={s.pricing}>
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`${s.priceCard} ${
                  plan.code === featuredCode ? s.priceCardFeatured : ""
                }`}
              >
                {plan.badge ? <span className={s.badge}>{plan.badge}</span> : null}
                <h3>{plan.name}</h3>
                {PLAN_POSITIONING[plan.code] ? (
                  <p className={s.pricePos}>{PLAN_POSITIONING[plan.code]}</p>
                ) : null}
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small>/ мес</small>
                </p>
                <ul className={s.priceList}>
                  {buildLandingPriceHighlights(plan.code).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link
                  to="/registraciya"
                  className={`${s.btn} ${
                    plan.code === featuredCode ? s.btnPrimary : s.btnSecondary
                  }`}
                  onClick={onTry}
                >
                  {TRIAL_DAYS} дней бесплатно
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <section id="trial" className={s.section}>
        <h2 className={s.sectionTitle}>Что входит в {TRIAL_DAYS} дней</h2>
        <p className={s.sectionLead}>
          Пробный период = тариф «Максимальный». Можно проверить весь продукт на
          своей группе.
        </p>
        <div className={s.capGrid}>
          {TRIAL_INCLUDES.map((text) => (
            <article key={text} className={s.capCard}>
              <h3>{text}</h3>
              <ul>
                <li>Без карты и без обязательств на старте</li>
              </ul>
            </article>
          ))}
        </div>
        <div className={s.promoGrid} style={{ marginTop: 24 }}>
          <article className={s.promoCard}>
            <div className={s.promoCopy}>
              <h3>Нужны детали по модулям?</h3>
              <p>
                Журнал, абонементы, CRM и приложение клуба — на отдельных
                посадочных.
              </p>
              <div className={s.heroActions}>
                <Link to="/zhurnal-poseshchaemosti" className={`${s.btn} ${s.btnSecondary}`}>
                  Журнал
                </Link>
                <Link to="/abonementy" className={`${s.btn} ${s.btnSecondary}`}>
                  Абонементы
                </Link>
                <Link
                  to="/crm-dlya-sportivnogo-kluba"
                  className={`${s.btn} ${s.btnSecondary}`}
                >
                  CRM
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Частые вопросы</h2>
        <p className={s.sectionLead}>
          Про цену, триал, телефон и то, что будет с данными после оплаты.
        </p>
        <div className={s.faqList}>
          {FAQ.map((item, index) => (
            <details key={item.q} className={s.faqItem} open={index === 0}>
              <summary>
                <span>{item.q}</span>
                <span className={s.faqChevron} aria-hidden />
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={s.ctaBand}>
        <h2>Начните с полного доступа</h2>
        <p>
          {TRIAL_DAYS} дней бесплатно на «Максимальном». Потом — от{" "}
          {LANDING_PRICE_FROM} ₽ в месяц.
        </p>
        <Link
          to="/registraciya"
          className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
          onClick={onTry}
        >
          Попробовать бесплатно
        </Link>
      </section>

      <footer className={s.footer}>
        <LegalFooterLinks />
      </footer>

      {!hasClub ? (
        <div className={s.sticky}>
          <Link
            to="/registraciya"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTry}
          >
            {TRIAL_DAYS} дней бесплатно
          </Link>
        </div>
      ) : null}
    </div>
  );
}
